import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { 
  StickyNote, 
  Plus, 
  Trash2,
  User,
  Truck,
  Coffee,
  Receipt,
  Sparkles,
  Pencil
} from 'lucide-react';
import { toast } from 'sonner';

const NotesPage = () => {
  const { currentLocation, api } = useAuth();
  const [notes, setNotes] = useState([]);
  const [entities, setEntities] = useState({ employees: [], suppliers: [], menu_items: [] });
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [newNote, setNewNote] = useState({ content: '', entity_type: 'general', entity_id: '' });
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (currentLocation) {
      fetchData();
    }
  }, [currentLocation]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [notesRes, empRes, supRes, menuRes] = await Promise.all([
        api.get('/notes', { params: { location_id: currentLocation.id } }),
        api.get('/employees', { params: { location_id: currentLocation.id } }),
        api.get('/suppliers', { params: { location_id: currentLocation.id } }),
        api.get('/menu-items', { params: { location_id: currentLocation.id } })
      ]);
      setNotes(notesRes.data);
      setEntities({
        employees: empRes.data,
        suppliers: supRes.data,
        menu_items: menuRes.data
      });
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = async () => {
    if (!newNote.content.trim()) {
      toast.error('Please enter note content');
      return;
    }

    try {
      await api.post('/notes', {
        location_id: currentLocation.id,
        content: newNote.content,
        entity_type: newNote.entity_type,
        entity_id: newNote.entity_id || null,
        source: 'manual'
      });
      toast.success('Note created');
      setCreateDialogOpen(false);
      setNewNote({ content: '', entity_type: 'general', entity_id: '' });
      fetchData();
    } catch (err) {
      console.error('Error creating note:', err);
      toast.error('Failed to create note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await api.delete(`/notes/${noteId}`);
      toast.success('Note deleted');
      setNotes(notes.filter(n => n.id !== noteId));
    } catch (err) {
      console.error('Error deleting note:', err);
      toast.error('Failed to delete note');
    }
  };

  const getEntityName = (note) => {
    if (!note || !note.entity_id) return null;
    
    switch (note.entity_type) {
      case 'employee':
        return entities.employees.find(e => e.id === note.entity_id)?.name;
      case 'supplier':
        return entities.suppliers.find(s => s.id === note.entity_id)?.name;
      case 'menu_item':
        return entities.menu_items.find(m => m.id === note.entity_id)?.name;
      default:
        return null;
    }
  };

  const getEntityIcon = (type) => {
    switch (type) {
      case 'employee': return User;
      case 'supplier': return Truck;
      case 'menu_item': return Coffee;
      case 'expense': return Receipt;
      default: return StickyNote;
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredNotes = filter === 'all' 
    ? notes 
    : filter === 'ai' 
      ? notes.filter(n => n.source === 'ai_insight')
      : notes.filter(n => n.entity_type === filter);

  if (loading) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6" data-testid="notes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Notes & Insights</h1>
          <p className="text-muted-foreground">Save important information for future reference</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-note-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Note
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Note</DialogTitle>
              <DialogDescription>
                Add a new note or save an insight for future reference
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Note Content</Label>
                <Textarea
                  value={newNote.content}
                  onChange={(e) => setNewNote({...newNote, content: e.target.value})}
                  placeholder="Enter your note..."
                  rows={4}
                  data-testid="new-note-content"
                />
              </div>
              <div className="space-y-2">
                <Label>Attach to Entity (Optional)</Label>
                <Select 
                  value={newNote.entity_type} 
                  onValueChange={(val) => setNewNote({...newNote, entity_type: val, entity_id: ''})}
                >
                  <SelectTrigger data-testid="new-note-entity-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General Note</SelectItem>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="supplier">Supplier</SelectItem>
                    <SelectItem value="menu_item">Menu Item</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newNote.entity_type !== 'general' && (
                <div className="space-y-2">
                  <Label>Select {newNote.entity_type.replace('_', ' ')}</Label>
                  <Select 
                    value={newNote.entity_id} 
                    onValueChange={(val) => setNewNote({...newNote, entity_id: val})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={`Choose ${newNote.entity_type.replace('_', ' ')}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {newNote.entity_type === 'employee' && entities.employees.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                      ))}
                      {newNote.entity_type === 'supplier' && entities.suppliers.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                      {newNote.entity_type === 'menu_item' && entities.menu_items.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateNote} data-testid="save-new-note-btn">
                Save Note
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="all">All ({notes.length})</TabsTrigger>
          <TabsTrigger value="ai">AI Insights ({notes.filter(n => n.source === 'ai_insight').length})</TabsTrigger>
          <TabsTrigger value="general">General ({notes.filter(n => n.entity_type === 'general').length})</TabsTrigger>
          <TabsTrigger value="employee">Employees ({notes.filter(n => n.entity_type === 'employee').length})</TabsTrigger>
          <TabsTrigger value="supplier">Suppliers ({notes.filter(n => n.entity_type === 'supplier').length})</TabsTrigger>
          <TabsTrigger value="menu_item">Menu Items ({notes.filter(n => n.entity_type === 'menu_item').length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Notes Grid */}
      {filteredNotes.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <StickyNote className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">No notes yet</h3>
            <p className="text-muted-foreground mb-4">
              {filter === 'all' 
                ? 'Create a note or save an AI insight to get started'
                : `No ${filter === 'ai' ? 'AI insights' : filter + ' notes'} found`}
            </p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Note
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredNotes.map((note) => {
            const Icon = getEntityIcon(note.entity_type);
            const entityName = getEntityName(note);
            
            return (
              <Card 
                key={note.id} 
                className="card-hover group cursor-pointer transition-all hover:ring-1 hover:ring-primary/20"
                onClick={() => {
                  setSelectedNote(note);
                  setViewDialogOpen(true);
                }}
                data-testid={`note-card-${note.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
                        note.source === 'ai_insight' ? 'bg-primary/10' : 'bg-muted'
                      }`}>
                        {note.source === 'ai_insight' ? (
                          <Sparkles className="w-4 h-4 text-primary" />
                        ) : (
                          <Icon className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          {note.source === 'ai_insight' && (
                            <Badge variant="secondary" className="text-xs">AI Insight</Badge>
                          )}
                          {entityName && (
                            <Badge variant="outline" className="text-xs">{entityName}</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteNote(note.id);
                      }}
                      data-testid={`delete-note-${note.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-foreground line-clamp-4 prose prose-sm prose-slate dark:prose-invert max-w-none">
                    <ReactMarkdown>{note.content}</ReactMarkdown>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    {formatDate(note.created_at)}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* View Note Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2">
              {selectedNote?.source === 'ai_insight' ? (
                <div className="w-8 h-8 rounded-md flex items-center justify-center bg-primary/10">
                  <Sparkles className="w-4 h-4 text-primary" />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-md flex items-center justify-center bg-muted">
                  {(() => {
                    const Icon = getEntityIcon(selectedNote?.entity_type);
                    return <Icon className="w-4 h-4 text-muted-foreground" />;
                  })()}
                </div>
              )}
              <div>
                <DialogTitle className="flex items-center gap-2">
                  {selectedNote?.source === 'ai_insight' && (
                    <Badge variant="secondary">AI Insight</Badge>
                  )}
                  {getEntityName(selectedNote) && (
                    <Badge variant="outline">{getEntityName(selectedNote)}</Badge>
                  )}
                  {!selectedNote?.source && !getEntityName(selectedNote) && 'Note'}
                </DialogTitle>
                <DialogDescription>
                  {selectedNote && formatDate(selectedNote.created_at)}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="py-4 prose prose-sm prose-slate dark:prose-invert max-w-none">
            <ReactMarkdown>{selectedNote?.content || ''}</ReactMarkdown>
          </div>
          <DialogFooter>
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => {
                if (selectedNote) {
                  handleDeleteNote(selectedNote.id);
                  setViewDialogOpen(false);
                }
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Note
            </Button>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotesPage;
